import { CloseOutlined } from '@ant-design/icons';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import hospitalIconUrl from 'assets/img/Hospital.svg';
import polygonBgUrl from 'assets/img/polygon-pod.svg';

import s from './styles.module.scss';

import type { IClinicFormValue } from '../../validation-schema';
import type { FC } from 'react';

interface IClinicAssignmentListProps {
  clinics: IClinicFormValue[];
  onRemove: (guid: string) => void;
}

const ClinicAssignmentList: FC<IClinicAssignmentListProps> = ({
  clinics,
  onRemove,
}) => {
  const { t } = useTranslation();
  const listRef = useRef<HTMLDivElement>(null);
  const prevCountRef = useRef(0);

  useEffect(() => {
    if (clinics.length > prevCountRef.current && listRef.current) {
      const lastCard = listRef.current.lastElementChild as HTMLElement | null;
      if (typeof lastCard?.scrollIntoView === 'function') {
        lastCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
    prevCountRef.current = clinics.length;
  }, [clinics.length]);

  if (clinics.length === 0) {
    return (
      <div className={s.emptyState} data-cy="clinic-list__empty">
        {t('labels.noClinicsSelected')}
      </div>
    );
  }

  return (
    <div ref={listRef} className={s.list} data-cy="clinic-assignment-list">
      {clinics.map((clinic) => (
        <div
          key={clinic.guid}
          className={s.card}
          data-cy={`clinic-card__${clinic.guid}`}
        >
          <div className={s.cardContent}>
            <div className={s.clinicIcon} data-cy="clinic-card__icon">
              <img
                className={s.polygonBg}
                src={polygonBgUrl}
                alt=""
                aria-hidden="true"
              />
              <img
                className={s.hospitalIcon}
                src={hospitalIconUrl}
                alt=""
                aria-hidden="true"
              />
            </div>
            <div className={s.clinicInfo}>
              <span className={s.clinicName}>{clinic.name}</span>
              <span className={s.clinicState}>{clinic.state}</span>
              <span className={s.clinicPatients}>
                {t('labels.nPatients', { count: clinic.patientCount })}
              </span>
            </div>
          </div>
          <button
            type="button"
            className={s.removeButton}
            onClick={() => onRemove(clinic.guid)}
            aria-label={t('labels.removeClinicAriaLabel', {
              clinicName: clinic.name,
            })}
            data-cy={`clinic-card__remove-${clinic.guid}`}
          >
            <CloseOutlined className={s.removeIcon} />
          </button>
        </div>
      ))}
    </div>
  );
};

export default ClinicAssignmentList;
